import Moment from 'moment';

import { ProductTypes } from '../types';
import { EXPIRED_STATUS } from '../console/_statics/policy.statics';
import type { Policy } from '../console/accounts/summary/types/policy.types';
import type { PolicyTableInfoDto } from '../types/policy/tables/policy-table-info.dto';

// True when today (UTC day) is on or before referenceDate + windowDays.
// Dates are midnight UTC; compare calendar days so the last day stays open.
export const isWithinP100GraceWindow = (
  referenceDate: string | Date | null | undefined,
  windowDays: number
): boolean => {
  if (!referenceDate) return false;
  const todayUtc = Moment.utc().startOf('day');
  const windowEndUtc = Moment.utc(referenceDate)
    .startOf('day')
    .add(windowDays, 'days');
  return todayUtc.isSameOrBefore(windowEndUtc, 'day');
};

// Cowbeller may bind/issue a P100 quote for 30 days after effectiveDate,
// even if the quote has expired.
export const P100_NB_BIND_WINDOW_DAYS = 30;

// cowbeller :Renewal bind/issue window: 60 days after effectiveDate/policyExpiry Date.
export const P100_RENEWAL_BIND_WINDOW_DAYS = 60;

export const getP100QuoteBindIssueWindowDays = (isRenewal?: boolean): number =>
  isRenewal ? P100_RENEWAL_BIND_WINDOW_DAYS : P100_NB_BIND_WINDOW_DAYS;

// Cowbeller may generate a renewal quote for 60 days after policy endDate.
export const P100_RENEWAL_CREATION_GRACE_WINDOW_DAYS = 60;

// Everyone else may generate a renewal quote for 21 days after policy endDate,
// matching the backend's p100RenewalExpirationWindow.
export const P100_AGENCY_RENEWAL_CREATION_WINDOW_DAYS = 21;

// The backend gives 60 days only when the token's account type is Cowbell.
export const getP100RenewalCreationWindowDays = (
  isCowbellUser: boolean
): number =>
  isCowbellUser
    ? P100_RENEWAL_CREATION_GRACE_WINDOW_DAYS
    : P100_AGENCY_RENEWAL_CREATION_WINDOW_DAYS;

type P100RenewalQuotePolicy = Pick<
  Policy,
  'status' | 'endDate' | 'isOpenForRenewal'
> &
  Partial<Pick<PolicyTableInfoDto, 'renewalQuoteIssued'>>;

// Nothing clears a P100 policy's flag by date, and its status can lag endDate, so endDate decides the cutoff.
export const isP100OpenForRenewalQuote = (
  policy: P100RenewalQuotePolicy,
  isCowbellUser: boolean
): boolean => {
  const isExpired = policy.status === EXPIRED_STATUS;
  const isWithinWindow = isWithinP100GraceWindow(
    policy.endDate,
    getP100RenewalCreationWindowDays(isCowbellUser)
  );
  if (policy.endDate && !isWithinWindow) return false;
  if (isCowbellUser) {
    return (
      policy.isOpenForRenewal ||
      (isExpired && isWithinWindow && !policy.renewalQuoteIssued)
    );
  }
  return policy.isOpenForRenewal && (!isExpired || isWithinWindow);
};

type AgencyRenewalQuotePolicy = Pick<
  Policy,
  'product' | 'status' | 'endDate' | 'isOpenForRenewal' | 'isPrimePlus'
>;

// With the P250 → P250X renewal migration on, the backend renews every P250
// policy on P250X, so agencies are offered no manual renewal for one.
export const renewsOnP250X = (
  product: string | undefined,
  isP250XRenewalMigrationOn: boolean
): boolean => isP250XRenewalMigrationOn && product === ProductTypes.p250;

// Agency menus always get the non-Cowbell window, jumped-in Cowbell users included.
export const canAgencyGenerateRenewalQuote = (
  policy: AgencyRenewalQuotePolicy,
  isP250XRenewalMigrationOn: boolean
): boolean => {
  if (policy.isPrimePlus) return false;
  if (renewsOnP250X(policy.product, isP250XRenewalMigrationOn)) return false;
  if (policy.product !== ProductTypes.p100) return policy.isOpenForRenewal;
  return isP100OpenForRenewalQuote(policy, false);
};
